import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, AlertTriangle } from 'lucide-react';
import { capsulesAPI, formatApiError } from '@/lib/api';
import { toast } from 'sonner';

export const CAPSULE_PURGE_PHRASE = 'BORRAR CAPSULA';

export default function PurgeCapsuleDialog({
  open,
  onOpenChange,
  tenantId,
  capsuleId,
  capsuleName,
  onSuccess,
}) {
  const [step, setStep] = useState(0);
  const [phrase, setPhrase] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setStep(0);
      setPhrase('');
      setLoading(false);
    }
  }, [open, capsuleId]);

  const handleClose = (v) => {
    if (!loading) onOpenChange(v);
  };

  const runPurge = async () => {
    if (phrase.trim() !== CAPSULE_PURGE_PHRASE) {
      toast.error(`Debe escribir exactamente: ${CAPSULE_PURGE_PHRASE}`);
      return;
    }
    setLoading(true);
    try {
      const res = await capsulesAPI.purgeHard(tenantId, capsuleId, { confirmation: CAPSULE_PURGE_PHRASE });
      toast.success('Cápsula eliminada por completo');
      onOpenChange(false);
      if (onSuccess) onSuccess(res.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const displayName = capsuleName ? `${capsuleId} (${capsuleName})` : capsuleId;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="rounded-sm max-w-md border-red-200" data-testid="purge-capsule-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base font-heading text-red-800">
            <AlertTriangle className="w-5 h-5 shrink-0" />
            {step === 0 ? 'Eliminar cápsula (irreversible)' : 'Confirmación final'}
          </DialogTitle>
          <DialogDescription className="text-left text-sm text-zinc-700 space-y-2 pt-2">
            {step === 0 ? (
              <>
                <p>
                  Se borrará de forma permanente la cápsula <span className="font-mono font-semibold">{displayName}</span> y
                  todo lo asociado en base de datos: conversaciones y mensajes, tickets, tareas del coordinador, workspaces,
                  feedback, logs de conversación, ejecuciones LLM, uso de tokens, notificaciones, diagnósticos de la cápsula,
                  agentes y configuración; se limpiarán enlaces en canales y en el equipo; se quitarán vectores de KB indexados
                  bajo rutas de tickets.
                </p>
                <p className="text-red-700 font-medium">Esta acción no se puede deshacer.</p>
              </>
            ) : (
              <>
                <p>
                  Paso 2 de 2: escriba <span className="font-mono font-semibold">{CAPSULE_PURGE_PHRASE}</span> y pulse
                  Eliminar definitivamente.
                </p>
                <div className="pt-2">
                  <Label htmlFor="purge-phrase" className="text-xs text-zinc-600">
                    Frase de confirmación
                  </Label>
                  <Input
                    id="purge-phrase"
                    autoComplete="off"
                    className="mt-1 rounded-sm font-mono text-sm"
                    placeholder={CAPSULE_PURGE_PHRASE}
                    value={phrase}
                    onChange={(e) => setPhrase(e.target.value)}
                    disabled={loading}
                  />
                </div>
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" className="rounded-sm" disabled={loading} onClick={() => handleClose(false)}>
            Cancelar
          </Button>
          {step === 0 ? (
            <Button
              type="button"
              className="rounded-sm bg-red-600 hover:bg-red-700 text-white"
              onClick={() => setStep(1)}
            >
              Continuar
            </Button>
          ) : (
            <Button
              type="button"
              className="rounded-sm bg-red-600 hover:bg-red-700 text-white"
              disabled={loading || phrase.trim() !== CAPSULE_PURGE_PHRASE}
              onClick={runPurge}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Eliminar definitivamente
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
